import { useState, useCallback } from 'react';
import { CRYPTO_FORM_VALIDATION_MESSAGES } from '../constants/cryptoModalConstants';
import { isValidPhoneNumber } from 'libphonenumber-js';

const NETWORK_VALIDATORS = {
  /*
  TRON (TRC20) Format:
  - Starts with 'T'
  - Followed by 33 Base58 characters
  - Total length: 34 characters
  */
  TRC20: (address) => 
    /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(address),

  /*
   Ethereum (ERC20) & Binance Smart Chain (BEP20) Format:
   - Starts with '0x'
   - Followed by exactly 40 Hexadecimal characters (a-f, A-F, 0-9)
   - Total length: 42 characters
   */
  ERC20: (address) => 
    /^0x[a-fA-F0-9]{40}$/.test(address),

  BEP20: (address) => 
    /^0x[a-fA-F0-9]{40}$/.test(address),

  /**
   * Bitcoin (BTC) Format - Matches 3 Types:
   * 1. Legacy (P2PKH): Starts with '1', Base58, 26-35 chars total.
   * 2. Pay-to-Script-Hash (P2SH): Starts with '3', Base58, 26-35 chars total.
   * 3. Bech32 (SegWit): Starts with 'bc1' or 'BC1', alphanumeric, 42-62 chars total.
   * NOTE: Bech32 must be all lowercase OR all uppercase, never mixed.
   */
  BTC: (address) => 
    /^(1|3)[1-9A-HJ-NP-Za-km-z]{25,34}$|^(bc1[a-z0-9]{39,59}|BC1[A-Z0-9]{39,59})$/.test(address),
};

export const validateCryptoInput = (
  address,
  currencyCode,
  networkCode,
  apiData
) => {
  const normalizedAddress = address?.trim();
  const currencyKey = currencyCode?.trim().toUpperCase();
  const networkKey = networkCode?.trim().toUpperCase();

  if (!normalizedAddress) {
    return {
      isValid: false,
      error: "Wallet address is required",
    };
  }

  // Assuming apiData is the array of currencies returned from /payment-methods
  const currency = apiData?.find(
    (c) => c.code === currencyKey
  );

  if (!currency) {
    return {
      isValid: false,
      error: "Unsupported currency",
    };
  }

  if (currency.kind !== "crypto") {
    return {
      isValid: false,
      error: "Only crypto wallets are allowed",
    };
  }

  const allowed = currency.networks.some(
    (n) => n.code.toUpperCase() === networkKey
  );

  if (!allowed) {
    return {
      isValid: false,
      error: `${networkKey} is not available for ${currencyKey}`,
    };
  }

  const validator = NETWORK_VALIDATORS[networkKey];

  if (!validator) {
    // If we don't have a specific regex for it, fallback to basic length check
    const isBasicValid = normalizedAddress.length >= 10;
    return {
      isValid: isBasicValid,
      error: isBasicValid ? null : "Invalid wallet address length",
    };
  }

  const isAddressValid = validator(normalizedAddress);

  return {
    isValid: isAddressValid,
    error: isAddressValid ? null : `Invalid ${networkKey} wallet address`,
  };
};

const useCryptoFormValidation = () => {
    const [errors, setErrors] = useState({});

    const validateForm = useCallback((formData, isOtpSent, paymentMethods) => {
        const newErrors = {};

        if (!formData.currency) {
            newErrors.currency = CRYPTO_FORM_VALIDATION_MESSAGES.CURRENCY_REQUIRED;
        }

        if (!formData.network) {
            newErrors.network = CRYPTO_FORM_VALIDATION_MESSAGES.NETWORK_REQUIRED;
        }

        if (formData.currency && formData.network) {
             const addressValidation = validateCryptoInput(
                 formData.wallet_address,
                 formData.currency,
                 formData.network,
                 paymentMethods
             );

             if (!addressValidation.isValid) {
                 newErrors.wallet_address = addressValidation.error;
             }
        } else if (!formData.wallet_address || formData.wallet_address.trim() === '') {
            newErrors.wallet_address = CRYPTO_FORM_VALIDATION_MESSAGES.WALLET_ADDRESS_REQUIRED;
        }

        if (!formData.wallet_label || formData.wallet_label.trim() === '') {
            newErrors.wallet_label = CRYPTO_FORM_VALIDATION_MESSAGES.WALLET_LABEL_REQUIRED;
        }



        if (isOtpSent && (!formData.otp || formData.otp.trim() === '')) {
            newErrors.otp = CRYPTO_FORM_VALIDATION_MESSAGES.OTP_REQUIRED;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, []);

    const clearError = useCallback((field) => {
        setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[field];
            return newErrors;
        });
    }, []);

    return { errors, validateForm, clearError, setErrors };
};

export default useCryptoFormValidation;
